import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { AuthService } from '../../services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [] 
}) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const token = AuthService.getToken();
        
        if (!token) {
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // Verify token with server
        const isValid = await AuthService.verifyToken(token);
        
        if (isValid) {
          // Check role permissions if required
          if (requiredRoles.length > 0) {
            const hasPermission = AuthService.hasAnyRole(requiredRoles);
            setIsAuthenticated(hasPermission);
          } else {
            setIsAuthenticated(true);
          }
        } else {
          // Token invalid, clear auth data
          AuthService.clearAuthData();
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        AuthService.clearAuthData();
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [requiredRoles]);

  if (loading) {
    return (
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: '#f5f5f5'
        }}
      >
        <Spin size="large" tip="Đang xác thực..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with return URL
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 